/**
 * The mailbox a letter leaves through ([22](../../../docs/22-the-mailbox-connection.md),
 * [ADR 0036](../../../docs/adr/0036-a-letter-to-a-stranger-leaves-through-the-customers-own-mailbox-connected-with-one-sign-in-and-never-from-a-joogoos-domain.md)).
 *
 * The one rule: Joogoos never writes to a stranger from a domain of its own. A letter to a prospect leaves through
 * the customer's own mailbox, as the customer, and sits in their sent folder. Our own sender, `hello@joogoos.com`, stays
 * for the app's own mail — sign-in links, receipts, reports.
 *
 * Nothing here carries a token. The refresh token is the one secret the customer hands us; it is encrypted at rest
 * and decrypted only in the process that sends, and no endpoint returns it, which is why `MailboxView` has no field
 * that could hold it.
 */

/** Which mailbox. Google first, Microsoft second — half of small businesses write from 365 (22). */
export const MAILBOX_PROVIDERS = ["google", "microsoft"] as const;
export type MailboxProvider = (typeof MAILBOX_PROVIDERS)[number];

/**
 * Where to send somebody to read a reply, in their own mailbox.
 *
 * A reply is in the customer's inbox and nowhere else — the tool stops the sequence and forwards the message, it
 * never holds the conversation (23 §follow) — so the one useful thing a screen can offer is a door to it. The
 * address is searched for rather than linked to directly, because the tool knows who wrote but not which message
 * of theirs is the reply.
 *
 * Keyed on the provider because there are two of them, and a screen that writes one provider's address in is a
 * screen that quietly does the wrong thing for the other. Microsoft has no consent flow yet (22), so today this
 * only ever answers Google's — but the assumption is here, named, rather than spelled into a component.
 */
export function mailboxSearchUrl(provider: MailboxProvider, email: string): string {
  const q = encodeURIComponent(email);
  return provider === "microsoft"
    ? `https://outlook.office.com/mail/0/search/id/${q}`
    : `https://mail.google.com/mail/u/0/#search/${q}`;
}

/**
 * `connected` · `disconnected` (the provider refused the refresh token, and the customer is told) · `paused` (the
 * customer's own switch). A mailbox that is not `connected` sends nothing, whatever else is true.
 */
export const MAILBOX_STATUSES = ["connected", "disconnected", "paused"] as const;
export type MailboxStatus = (typeof MAILBOX_STATUSES)[number];

/**
 * The box's own switch (`OUTREACH_SENDING`), checked on the last line before the provider.
 *
 * `off` is the default everywhere. `allowlist` is what a dev box rehearses on: every address must be in
 * `OUTREACH_SEND_ONLY_TO`, so no bug above it can write to a company. `on` is live, and only live.
 */
/**
 * What a box will let out, narrowest first (22 §sending, [ADR 0041](../../../docs/adr/0041-a-rehearsal-delivers-every-letter-to-one-address-so-the-loop-can-be-tried-on-the-real-list.md)).
 *
 * `off` sends nothing. `allowlist` **refuses** anyone who is not us. `rehearsal` **redirects** everyone to us,
 * with the real recipient in the subject — which is the difference that matters: on `allowlist` the real list
 * cannot be rehearsed against at all, because every real company is refused, and the only thing that can be tried
 * is a letter addressed to ourselves. `on` is live.
 */
export const SENDING_GATES = ["off", "allowlist", "rehearsal", "on"] as const;
export type SendingGate = (typeof SENDING_GATES)[number];

/**
 * Mailboxes whose provider caps them low and whose address reads as spam to a company.
 *
 * This list refuses nothing. A sole trader with only a Gmail address can still write ten letters a day; the page
 * says one honest line about it and suggests an address on the business's own domain before a sequence is switched
 * on (22). It is the same list the judge refuses a *prospect* on, which is a different question with the same facts:
 * a company writing from `hotmail.com` is not a company we can find a real address for.
 */
export const FREE_MAIL_DOMAINS: ReadonlySet<string> = new Set([
  "gmail.com", "googlemail.com", "outlook.com", "hotmail.com", "hotmail.co.uk", "live.com", "msn.com",
  "yahoo.com", "yahoo.co.uk", "ymail.com", "aol.com", "icloud.com", "me.com", "mac.com", "gmx.com", "gmx.net",
  "proton.me", "protonmail.com", "mail.com", "zoho.com", "yandex.com", "free.fr", "orange.fr", "wanadoo.fr", "laposte.net",
]);

/** The domain of an address, lower-cased, or null when it is not an address at all. */
export function mailDomain(address: string): string | null {
  const at = address.lastIndexOf("@");
  if (at < 1 || at === address.length - 1) return null;
  return address.slice(at + 1).trim().toLowerCase();
}

export function isFreeMailbox(address: string): boolean {
  const domain = mailDomain(address);
  return domain !== null && FREE_MAIL_DOMAINS.has(domain);
}

/** The ceiling no customer's cap may pass. Google Workspace allows 2,000 a day and free Gmail 500; neither is a cold-mail number (22). */
export const MAILBOX_CAP_CEILING = 100;
/** What a mailbox may send each day of its first week, whatever cap was set. */
export const MAILBOX_RAMP_PER_DAY = 5;
/** How long the ramp lasts, in days. */
export const MAILBOX_RAMP_DAYS = 7;

/**
 * How many letters this mailbox may send today: five a day for the first week, then the customer's cap.
 *
 * The ramp is measured from the day the mailbox began sending rather than the day it was connected, so a mailbox
 * connected in September and first used in November does not arrive already warmed. A cap is clamped rather than
 * refused — a stored row from before the ceiling moved must not be able to send more than the ceiling.
 */
export function rampedCap(cap: number, warmedFrom: Date | null, now: Date = new Date()): number {
  const ceiling = Math.max(0, Math.min(Math.trunc(cap), MAILBOX_CAP_CEILING));
  if (!warmedFrom) return Math.min(ceiling, MAILBOX_RAMP_PER_DAY);
  const days = Math.floor((now.getTime() - warmedFrom.getTime()) / 86_400_000);
  return days < MAILBOX_RAMP_DAYS ? Math.min(ceiling, MAILBOX_RAMP_PER_DAY) : ceiling;
}

/** The legal footer, read from the business page and the country sent from — never typed on the mailbox page (22). */
export interface MailboxFooter {
  /** The company as its business page names it. */
  company: string;
  /** Its postal address, as one line, or null when the page does not carry one yet. */
  address: string | null;
  /** Its website, for the sign-off above the footer. Null when the Business page has none. */
  website: string | null;
}

/** The mailbox as the page draws it. Everything here is safe to send to a browser; the token is not in it. */
export interface MailboxView {
  provider: MailboxProvider;
  address: string;
  displayName: string | null;
  signsAsName: string;
  signsAsRole: string;
  status: MailboxStatus;
  /** What the customer set, at most `MAILBOX_CAP_CEILING`. */
  dailyCap: number;
  /** What today's cap really is, the warm-up applied. */
  allowedToday: number;
  sentToday: number;
  /** The day the ramp began — the first send — or null while nothing has been sent. */
  warmedFrom: string | null;
  connectedAt: string;
  lastSentAt: string | null;
  /** Whatever the provider last refused with, in words, or null. Never a token. */
  lastError: string | null;
  /** True when the address is on a free provider: one line of warning on the page, and no refusal (22). */
  free: boolean;
  /**
   * Whether a reply to a letter from this mailbox will be noticed
   * ([ADR 0044](../../../docs/adr/0044-a-reply-is-noticed-by-reading-the-thread-it-belongs-to-and-a-cold-letter-carries-no-address-of-ours.md)).
   *
   * False for every mailbox connected before 2026-09-21, which was granted sending alone. Those letters keep the
   * reply address they always had and the page asks their owner to connect the mailbox again. Never a refusal: a
   * mailbox that can only send is the product as it shipped, and worse rather than broken.
   */
  canReadReplies: boolean;
}

/** `GET /v1/mailbox`. */
export interface MailboxResponse {
  /** False when this box has no mailbox table yet, the way every other screen says "not built". */
  available: boolean;
  mailbox: MailboxView | null;
  /** Whether this workspace's own switch is on, beside the box's. */
  sendingOn: boolean;
  /** The box's switch, so the page can say plainly that nothing can reach a company from here. */
  gate: SendingGate;
  /**
   * Whether this server receives replies at a reply address of its own (22 §replies, the fallback the owner chose on
   * 2026-10-06 for a mailbox that can only send). Then such a mailbox's letters carry it, a reply stops the follow-ups
   * and is forwarded to the owner, and the page says so instead of *one letter, no follow-ups*.
   */
  repliesByAddress: boolean;
  /** The footer that will be printed under every letter, read from the business page. */
  footer: MailboxFooter;
  /** Only an owner may connect, disconnect or change any of this (22). */
  mayConnect: boolean;
  note?: string;
}

/** `POST /v1/mailbox/connect`: where to send the customer for the one sign-in that asks only to send. */
export interface ConnectMailboxResponse {
  url: string;
}

/** `PATCH /v1/mailbox`: the three things the customer may change. */
export interface MailboxSettings {
  signsAsName?: string | undefined;
  signsAsRole?: string | undefined;
  dailyCap?: number | undefined;
  /** The workspace's own pause, which is not a disconnection: the token stays and nothing leaves. */
  paused?: boolean | undefined;
}

/**
 * What the connect flow asks Google for: sending, and the address it will send as
 * ([ADR 0044](../../../docs/adr/0044-a-reply-is-noticed-by-reading-the-thread-it-belongs-to-and-a-cold-letter-carries-no-address-of-ours.md),
 * amended 2026-09-27).
 *
 * `gmail.readonly` was asked for from 2026-09-21 so that a reply could be noticed in its thread, and it is
 * *restricted*: offering it to the public costs a yearly outside security assessment (14). Until that is worth
 * paying for, the connection asks to send and nothing else, and a mailbox that cannot see its replies sends one
 * letter per company and no follow-ups (`repliesNoticed`). A mailbox that was granted reading keeps it.
 *
 * `GOOGLE_READ_SCOPE` stays named because grants made while it was asked for carry it, and it is what says a
 * mailbox can read its own replies. Read-only rather than `modify`: noticing a reply never needs writing.
 */
export const GOOGLE_READ_SCOPE = "https://www.googleapis.com/auth/gmail.readonly";
export const GOOGLE_SEND_SCOPE = "https://www.googleapis.com/auth/gmail.send";
export const GOOGLE_SEND_SCOPES = [GOOGLE_SEND_SCOPE, "openid", "email"] as const;

/**
 * Whether this grant lets us notice a reply.
 *
 * A mailbox connected before ADR 0044 has the send scope alone, and Google issues scopes on a consent rather than
 * retroactively — so it keeps working and keeps carrying a reply address, and the page asks its owner to connect
 * it again. There is no migration that can do this for somebody.
 */
export function canReadReplies(scopes: readonly string[]): boolean {
  return scopes.some((s) => s.trim() === GOOGLE_READ_SCOPE);
}

/**
 * Whether a reply to this mailbox's letters would be noticed at all: by reading the thread, or through a reply
 * address of ours when the box receives one (22). **A follow-up goes only where a reply would stop it** — writing
 * again to somebody who already answered "no" is how a cold sender is reported, so a mailbox that would not know
 * sends the first letter and nothing after it, and the answer lands in the owner's own inbox like any other.
 */
export function repliesNoticed(scopes: readonly string[], replyAddress: boolean): boolean {
  return canReadReplies(scopes) || replyAddress;
}

/**
 * What Search Console is asked for ([21 §13](../../../docs/21-articles-and-seo.md#13--what-google-search-console-says)).
 *
 * `webmasters.readonly` and nothing more: it reads what Google already tells the site's owner about their own
 * site, and cannot change a verification, submit a sitemap or ask for a page to be removed. It is *sensitive*
 * rather than restricted, so it costs a consent screen review and not a yearly security assessment.
 */
export const GOOGLE_SEARCH_CONSOLE_SCOPES = ["https://www.googleapis.com/auth/webmasters.readonly", "openid", "email"] as const;

/** How long a half-finished connection is allowed to sit in `mailbox_connects` before it is worthless. */
export const MAILBOX_CONNECT_TTL_MS = 10 * 60 * 1000;
